import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import ConfettiExplosion from "react-confetti-explosion";
import { useLocation } from "react-router-dom";
import useHandleRedirect from "../../Hooks/useHandleRedirect";
import { useSelector, useDispatch } from 'react-redux';
import { get } from "../../Services/ApiService";
import { ServerEndpoint } from "../../Redux/endpoints.ts";
import { clearCart } from "../../Redux/Cart/CartActions";
import Layout from "../../Utils/Navbar/navbar-layout-component";
import Footer from "../../Utils/Footer/footer-component";
import MediaComponent from "../../Utils/Media/media-component";
import SystemDataService from '../../Services/SystemDataService';
import { ButtonFlowHover, ButtonChevronReveal } from "../../Utils/Buttons/button-component";
import { getPaymentSuccessData } from "../../Public/payment-success-data.js";

const DELIVERY_STEPS = ['paid', 'received', 'processing', 'ready_for_shipping', 'shipped', 'delivered', 'done'];
const PICKUP_STEPS   = ['paid', 'received', 'processing', 'ready_for_pickup', 'picked_up', 'done'];

const PaymentSuccess = () => {
    const navigate = useHandleRedirect();
    const dispatch = useDispatch();
    const location = useLocation();
    const { links = [], language = 'en' } = useSelector((state) => state.systemData || {});
    const txt = useMemo(() => getPaymentSuccessData(language), [language]);

    const deliveryRegistry = useMemo(() => [
        { key: 'paid',               asset: SystemDataService.byLabel(links, 'Status Paid Icon'),       fallbackLabel: txt.statuses.paid.fallbackLabel },
        { key: 'received',           asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.received.fallbackLabel },
        { key: 'processing',         asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.processing.fallbackLabel },
        { key: 'ready_for_shipping', asset: SystemDataService.byLabel(links, 'Status Shipped Icon'),    fallbackLabel: txt.statuses.ready_for_shipping.fallbackLabel },
        { key: 'shipped',            asset: SystemDataService.byLabel(links, 'Status Shipped Icon'),    fallbackLabel: txt.statuses.shipped.fallbackLabel },
        { key: 'delivered',          asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.delivered.fallbackLabel },
        { key: 'done',               asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.done.fallbackLabel },
    ], [links, txt]);

    const pickupRegistry = useMemo(() => [
        { key: 'paid',             asset: SystemDataService.byLabel(links, 'Status Paid Icon'),       fallbackLabel: txt.statuses.paid.fallbackLabel },
        { key: 'received',         asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.received.fallbackLabel },
        { key: 'processing',       asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.processing.fallbackLabel },
        { key: 'ready_for_pickup', asset: SystemDataService.byLabel(links, 'Status Processing Icon'), fallbackLabel: txt.statuses.ready_for_pickup.fallbackLabel },
        { key: 'picked_up',        asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.picked_up.fallbackLabel },
        { key: 'done',             asset: SystemDataService.byLabel(links, 'Status Delivered Icon'),  fallbackLabel: txt.statuses.done.fallbackLabel },
    ], [links, txt]);

    const [showConfetti, setShowConfetti] = useState(true);
    const [order, setOrder] = useState(null);
    const [orderLoading, setOrderLoading] = useState(true);

    const orderId = new URLSearchParams(location.search).get('orderId');
    const fromCheckout = new URLSearchParams(location.search).get('fromCheckout') === '1';

    useEffect(() => {
        window.scrollTo(0, 0);
        if (fromCheckout) dispatch(clearCart());
        const timer = setTimeout(() => setShowConfetti(false), 2500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!orderId) { setOrderLoading(false); return; }
        get(`${ServerEndpoint.GET_ORDER}/${orderId}`, true)
            .then(res => { if (res?.data?.data) setOrder(res.data.data); })
            .catch(() => {})
            .finally(() => setOrderLoading(false));
    }, [orderId]);

    const isPickup = order ? !order.includeDelivery : false;
    const activeSteps    = isPickup ? PICKUP_STEPS    : DELIVERY_STEPS;
    const statusRegistry = isPickup ? pickupRegistry  : deliveryRegistry;

    const currentStatusIndex = useMemo(() => {
        const idx = activeSteps.indexOf(order?.status || 'paid');
        return idx >= 0 ? idx : 0;
    }, [order?.status, activeSteps]);

    const trackLink = orderId ? `${window.location.origin}/track?id=${orderId}` : '';

    const progressPct = currentStatusIndex === 0 ? 0 : (currentStatusIndex / (activeSteps.length - 1)) * 100;

    const courierInfo = useMemo(() => SystemDataService.courierInfo(links, order), [order, links]);

    return (
        <div className="bg-gray-50 min-h-screen font-primary">
            {showConfetti && (
                <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
                    <ConfettiExplosion force={0.8} duration={3000} particleCount={150} width={1600} colors={['#0F172A', '#F97316', '#CBD5E1']} />
                </div>
            )}

            <Layout>
                <main className="max-w-5xl mx-auto px-6 py-16 lg:py-24">

                    {/* ── HEADER ── */}
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="mb-14"
                    >
                        <p className="text-xs font-bold text-primary-950/30 uppercase tracking-[0.3em] mb-2">
                            {txt.header.eyebrow}
                        </p>
                        <h1
                            className="text-6xl lg:text-8xl font-black text-primary-950 uppercase er italic leading-none"
                            style={{ fontFamily: 'Impact, sans-serif' }}
                        >
                            {txt.header.title}
                        </h1>
                        {orderId && (
                            <p className="text-sm font-medium text-gray-400 mt-3">
                                {txt.header.registryId}:{' '}
                                <span className="text-primary-950 font-black">{orderId}</span>
                            </p>
                        )}
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                        {/* ── STATUS TRACKER ── */}
                        <motion.div
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                            className="lg:col-span-5 bg-primary-950 rounded-[2rem] p-8 flex flex-col gap-8"
                        >
                            <p className="text-xs font-bold text-white/30 uppercase tracking-widest">
                                {txt.progress.sectionLabel}
                            </p>

                            {/* Vertical stepper */}
                            <div className="relative flex flex-col">
                                {/* Background rail */}
                                <div className="absolute left-[15px] top-5 bottom-5 w-0.5 bg-white/10" />
                                {/* Progress fill */}
                                <div
                                    className="absolute left-[15px] top-5 w-0.5 bg-secondary-500 transition-all duration-1000"
                                    style={{ height: `${progressPct}%` }}
                                />

                                {statusRegistry.map((entry, i) => {
                                    const isDone = i <= currentStatusIndex;
                                    const isActive = i === currentStatusIndex;
                                    return (
                                        <motion.div
                                            key={entry.key}
                                            initial={{ x: -10, opacity: 0 }}
                                            animate={{ x: 0, opacity: isDone ? 1 : 0.3 }}
                                            transition={{ delay: 0.3 + i * 0.1 }}
                                            className="flex items-center gap-4 py-4 relative"
                                        >
                                            {/* Step dot */}
                                            <div className={`w-8 h-8 rounded-full shrink-0 z-10 flex items-center justify-center transition-all duration-500
                                                ${isDone ? 'bg-secondary-500' : 'bg-white/10'}
                                                ${isActive ? 'ring-4 ring-secondary-500/30 scale-110' : ''}`}
                                            >
                                                {isDone && !isActive ? (
                                                    <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <MediaComponent media={entry.asset.image} className={`w-4 h-4 p-1 ${isDone ? 'filter brightness-0 invert' : 'filter grayscale opacity-50'}`} useSekeleton={false} />
                                                )}
                                            </div>
                                            <div>
                                                <p className={`text-sm font-bold leading-tight ${isDone ? 'text-white' : 'text-white/30'}`}>
                                                    {entry.fallbackLabel}
                                                </p>
                                                {isActive && (
                                                    <p className="text-xs text-secondary-500 font-bold mt-0.5">{txt.progress.current}</p>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Tracking link */}
                            {order?.trackingUrl && (
                                <a
                                    href={order.trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 transition-all text-white text-sm font-black uppercase tracking-wide"
                                >
                                    {txt.tracking.btn}
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                    </svg>
                                </a>
                            )}
                        </motion.div>

                        {/* ── ORDER MANIFEST ── */}
                        <motion.div
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.25 }}
                            className="lg:col-span-7 bg-white rounded-[2rem] border border-gray-100 shadow-2xl shadow-gray-200/30 flex flex-col overflow-hidden"
                        >
                            {/* Card header */}
                            <div className="px-8 py-7 border-b border-gray-50 flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest mb-1">
                                        {txt.manifest.title}
                                    </p>
                                    {order?.email && (
                                        <p className="text-sm font-medium text-gray-500">{order.email}</p>
                                    )}
                                </div>
                                <p className="text-3xl font-black text-secondary-500 italic er shrink-0">
                                    ${order?.total != null ? order.total.toFixed(2) : txt.manifest.noTotal}
                                </p>
                            </div>

                            {/* Shipment method */}
                            {order && (
                                <div className="px-8 py-5 border-b border-gray-50 bg-gray-50/40">
                                    {courierInfo ? (
                                        <div className="flex items-center gap-4">
                                            <div className="h-10 w-16 bg-white rounded-xl border border-gray-100 flex items-center justify-center p-2 shrink-0 shadow-sm">
                                                {courierInfo.logoUrl
                                                    ? <img src={courierInfo.logoUrl} alt={courierInfo.name} className="h-full w-full object-contain" />
                                                    : <span className="text-xs font-black text-primary-950">{courierInfo.name}</span>
                                                }
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-primary-950/30 uppercase tracking-widest leading-none mb-1">Ships via</p>
                                                <p className="text-sm font-black text-primary-950">{courierInfo.name}</p>
                                                {courierInfo.transitDays && (
                                                    <p className="text-xs text-gray-400 mt-0.5">{courierInfo.transitDays} business days</p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-4">
                                            <div className="h-10 w-10 bg-white rounded-xl border border-gray-100 flex items-center justify-center shrink-0 shadow-sm">
                                                <svg className="w-5 h-5 text-primary-950/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-primary-950/30 uppercase tracking-widest leading-none mb-1">Order Type</p>
                                                <p className="text-sm font-black text-primary-950">Store Pickup</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Items */}
                            <div className="p-6 space-y-2.5 overflow-y-auto max-h-64">
                                {orderLoading ? (
                                    <div className="flex items-center justify-center h-32">
                                        <div className="w-5 h-5 border-2 border-primary-950 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : !order ? (
                                    <div className="flex items-center justify-center h-32">
                                        <p className="text-sm font-medium text-gray-300">{txt.manifest.empty}</p>
                                    </div>
                                ) : (order.items || []).map((item, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 + i * 0.07 }}
                                        className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50/60 hover:bg-gray-100/40 transition-colors"
                                    >
                                        <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-white border border-gray-100 p-1.5">
                                            <MediaComponent media={item.image} className="w-full h-full object-contain mix-blend-multiply" useSekeleton={false} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-sm text-primary-950 truncate">{item.name}</p>
                                            <p className="text-xs font-medium text-gray-400 mt-0.5">× {item.quantity}</p>
                                        </div>
                                        <p className="font-black text-sm text-primary-950 italic shrink-0">
                                            ${(item.price * item.quantity).toFixed(2)}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Pricing breakdown */}
                            {order && (
                                <div className="px-6 pb-4 space-y-2 border-t border-gray-50 pt-4">
                                    {(() => {
                                        const subtotal = (order.items || []).reduce((s, it) => s + it.price * it.quantity, 0);
                                        const shipping = courierInfo?.cost || 0;
                                        return (
                                            <>
                                                <div className="flex justify-between items-center">
                                                    <p className="text-xs font-bold text-gray-400">Subtotal</p>
                                                    <p className="text-xs font-bold text-gray-500">${subtotal.toFixed(2)}</p>
                                                </div>
                                                {shipping > 0 && (
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-1.5">
                                                            {courierInfo?.logoUrl && (
                                                                <img src={courierInfo.logoUrl} alt={courierInfo.name} className="h-3 w-auto object-contain" />
                                                            )}
                                                            <p className="text-xs font-bold text-gray-400">{courierInfo.name}</p>
                                                        </div>
                                                        <p className="text-xs font-bold text-gray-500">${Number(shipping).toFixed(2)}</p>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                                                    <p className="text-xs font-bold text-gray-400">Total</p>
                                                    <p className="text-xl font-black text-primary-950 italic er">${order.total?.toFixed(2)}</p>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Tracking link */}
                            {order?.trackingUrl && (
                                <div className="px-6 pb-4">
                                    <a
                                        href={order.trackingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between w-full px-5 py-3.5 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                                    >
                                        <span className="text-xs font-black text-primary-950">{txt.tracking.btn || 'Track Shipment'}</span>
                                        <svg className="w-4 h-4 text-secondary-500 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                        </svg>
                                    </a>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="px-6 pb-6 pt-2 border-t border-gray-50 grid sm:grid-cols-2 gap-3">
                                <ButtonFlowHover onClick={() => navigate('/shop')} fullWidth size="md">
                                    {txt.actions.sourceMore}
                                </ButtonFlowHover>
                                <ButtonChevronReveal onClick={() => navigate('/')} fullWidth size="md">
                                    {txt.actions.exitRegistry}
                                </ButtonChevronReveal>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── LIVE TRACKING BUTTON ── */}
                    {trackLink && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                            className="mt-8 flex justify-center"
                        >
                            <a
                                href={trackLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 bg-primary-950 hover:bg-primary-800 transition-colors rounded-2xl px-8 py-4"
                            >
                                <span className="w-2 h-2 rounded-full bg-secondary-500 animate-pulse shrink-0" />
                                <span className="text-sm font-black uppercase tracking-widest text-white">
                                    {txt.tracking.trackLiveBtn}
                                </span>
                                <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        </motion.div>
                    )}
                </main>
            </Layout>
            <Footer />
        </div>
    );
};

export default PaymentSuccess;
